import { useQuery } from '@apollo/client';
import { faEyeSlash, faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, Outlet } from 'react-router';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import {
  BookmarkParentType,
  BookmarkTargetType,
  ReviewCommentParentType,
  Role,
} from '~/apollo/generated/v3/graphql';
import { graphql } from '~/apollo/generated/v4';
import type { StudyPageQuery } from '~/apollo/generated/v4/graphql';
import { RoleSecured } from '~/components/auth/RoleSecured';
import { TargetBookmarksManagerModal } from '~/components/bookmark/TargetBookmarksManagerModal';
import { Heading } from '~/components/common/Heading';
import { GeologyTypeIconV4 } from '~/components/common/icons/geology-type-icon-v4';
import { NotFound } from '~/components/common/NotFound';
import { PageHeading } from '~/components/common/PageHeading';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import type { BreadcrumbItem } from '~/components/layout/Breadcrumb';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import { TargetReviewCommentsModal } from '~/components/review-comment/TargetReviewCommentsModal';
import { Alert } from '~/components/ui/alert';
import { SODetailText } from '~/components/upload/supporting-object/so-detail-text';
import { useRouteParam } from '~/hooks/routing';
import * as routes from '~/paths';
import { StudyRouteNavigation } from '~/routes/study/__StudyRouteNavigation';
import { studyGeologyTypes } from '~/utils/modules/study';
import { isPublished } from '~/utils/modules/supporting-object';

const STUDY_PAGE = graphql(`
  query StudyPage($id: ID!) {
    studyGet(id: $id) {
      ...studyParts
      outcrops {
        id
        name
        region {
          id
          name
        }
      }
      keyParameters {
        id
        geologyType
      }
    }
  }
`);

export function prependBreadcrumb(
  study?: NonNullable<StudyPageQuery['studyGet']> | null,
): BreadcrumbItem[] {
  if (!study) return [];

  const breadcrumb: BreadcrumbItem[] = [];

  // If the study only belongs to a single outcrop,
  // prepend the outcrop's region and outcrop name to the breadcrumb
  if (study.outcrops.length === 1) {
    const oc = study.outcrops[0];
    if (!oc.region) {
      throw new Error('study.outcrops.region is not loaded');
    }

    breadcrumb.push({
      routeId: routes.regionRoute(parseInt(oc.region.id)),
      pathname: routes.regionRoute(parseInt(oc.region.id)),
      title: oc.region.name,
    });
    breadcrumb.push({
      routeId: routes.outcropRoute(parseInt(oc.id)),
      pathname: routes.outcropRoute(parseInt(oc.id)),
      title: oc.name,
    });
  }

  return breadcrumb;
}

export default function StudyPage() {
  return (
    <ApolloProviderV4>
      <StudyPageInner />
    </ApolloProviderV4>
  );
}

function StudyPageInner() {
  const studyId = useRouteParam('studyId', parseInt);

  const { data, loading } = useQuery(STUDY_PAGE, {
    variables: { id: studyId },
  });

  const study = data?.studyGet;

  useBreadcrumb(
    'routes/study/$studyId',
    study?.name ?? 'Study',
    null,
    prependBreadcrumb(study),
  );

  if (loading) return <SpinnerPlaceholder />;
  if (!study) return <NotFound />;

  const geologyTypes = studyGeologyTypes(study);

  return (
    <div className="space-y-4">
      {!isPublished(study) && (
        <Alert status="info">
          <FontAwesomeIcon icon={faEyeSlash} className="text-2xl" />

          <div>
            <Heading level={3}>Study Not Approved</Heading>
            <p>
              This outcrop has not been approved and is only visible to admins.
            </p>
          </div>
        </Alert>
      )}

      <div className="flex justify-between items-end">
        <PageHeading>
          {study.name}
          <span className="space-x-1 ml-2">
            {geologyTypes.map(gt => (
              <GeologyTypeIconV4
                key={gt}
                dominant={study.dominantGeologyType === gt}
                geologyType={gt}
                className="w-8 h-8"
              />
            ))}
          </span>
        </PageHeading>

        <div className="space-x-1">
          <TargetBookmarksManagerModal
            parentType={BookmarkParentType.Study}
            parentId={studyId}
            targetType={BookmarkTargetType.Study}
            targetId={studyId}
            path={routes.studyRoute(studyId)}
          />

          <TargetReviewCommentsModal
            parentType={ReviewCommentParentType.Study}
            parentId={parseInt(study.id)}
            name={study.name}
          />

          <RoleSecured roles={[Role.RoleAdmin]}>
            <Link
              to={routes.uploadStudyUpdateRoute(studyId)}
              className="btn btn-ghost btn-sm gap-1"
            >
              <FontAwesomeIcon icon={faPencil} /> Edit
            </Link>
          </RoleSecured>
        </div>
      </div>

      <div>
        <SODetailText label="Author" value={study.collectedBy} />
        <SODetailText label="Year" value={study.collectedYear} />
        <SODetailText
          label="Publication type"
          value={study.publicationType?.join(', ')}
        />
      </div>

      <div className="grid lg:grid-cols-6 gap-4 mt-4">
        <StudyRouteNavigation studyId={studyId} />

        <div className="lg:col-span-5">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
